/**
 * The deck desktop screen's single Enter precedence order — the descending
 * twin of `utils/deckEscapeLadder`.
 *
 * **Why this exists.** Enter was claimed in two places with different
 * conditions. A window listener (`DeckDesktopPanels`) ran only while
 * `document.activeElement` was the body or the list scrollport, and meant
 * "open the first card" or "edit the selected one" depending on state. A card
 * ROW's own `onKeyDown` ran whenever that row had focus and always meant
 * "select this card", whatever the screen state was. Neither could see the
 * other, and which one you got depended on how you had arrived:
 *
 *   - from a standing start: Enter selected card 1, Enter edited it, a third
 *     press landed in the Term field and did nothing.
 *   - having tabbed to a row, OR having clicked one (a click focuses the row):
 *     Enter selected the card, and every press after that re-selected the
 *     same card — editing was unreachable by keyboard, while the pane went on
 *     advertising "Enter to edit".
 *
 * So the key that opened editing a moment ago stopped doing so as soon as the
 * reader touched a row, which is the defect this module closes.
 *
 * **The model, stated before the order.** The screen has two facts and one
 * authority:
 *
 *  - **Selection is the screen's single notion of "the current card."** The
 *    reading pane renders it, the edit session belongs to it, the Escape
 *    ladder reads it, and — from here on — Enter's meaning is decided by it.
 *  - **Focus is where keystrokes go**, and nothing else. It never competes
 *    with the selection to say which card is current. Inside the list, focus
 *    FOLLOWS the selection: the list holds exactly one tab stop, the selected
 *    row (or the first row when nothing is selected).
 *
 * This does not contradict brief §22's "on desktop, selection and focus are
 * different things — a row can be selected while the caret is elsewhere". That
 * sentence is about the caret being in a FIELD of the pane while a row stays
 * selected, which still holds. What it never licensed is two different rows
 * competing to be current, one holding focus and one holding the selection.
 *
 * **What each key does:**
 *
 *  - **Tab** walks the screen's tab order and never changes the selection.
 *    Because the list is one stop, the pane and the controls beyond it are a
 *    bounded number of presses away no matter how large the deck is.
 *  - **↑ / ↓**, with a row focused, move the selection to the previous / next
 *    card for READING, focus riding along. This is the list's own movement,
 *    and it is what replaces tabbing through every row.
 *  - **Enter** descends exactly one level of the screen, per the order below.
 *  - **Escape** ascends, per `deckEscapeLadder`. The two compose: `read` →
 *    `edit` is Enter's rung 5, and `edit` → `read` is Escape's rung
 *    `edit-session`; `read` → nothing-selected is Escape's `selection`, and
 *    nothing-selected → `read` is Enter's rung 6.
 *
 * **The order, outermost-yields-to-innermost:**
 *
 *  1. `overlay`    — a modal, sheet, popover or menu is open. Yield: Enter
 *                    inside an overlay belongs to the overlay (a confirm
 *                    button, a name being committed), exactly as Escape rung 1
 *                    yields rather than closing.
 *  2. `field`      — the caret is in a card field. Yield, always. What Enter
 *                    does there is decided by ADR-067's amendment of
 *                    2026-07-30: a line break in every side-B field, and
 *                    prohibited in Term (which is why Term is an `<input>` and
 *                    side B a `<textarea>`). This ladder must never claim that
 *                    press, and in particular must never spend it on crossing
 *                    a card boundary — the same amendment forbids repurposing
 *                    a key for that.
 *  3. `focus-row`  — a row holds focus and it is NOT the selected card. Select
 *                    it, for reading. This is the one rung that reads focus,
 *                    and it reads it to RESOLVE the disagreement rather than
 *                    to act on a second notion of current: after it fires, the
 *                    two agree and every later press takes the rungs below.
 *  4. `out-of-view` — a card is selected but the list is not showing it: a
 *                    search or a filter is narrowing the rows and the selected
 *                    card is not among them. **Yield — this level does not
 *                    claim Enter**, for the same reason rung 4 below does not:
 *                    there is nothing on screen to descend into. Without this
 *                    rung the press resolved to `read` and armed an edit
 *                    session on a card nobody could see — the press looked
 *                    dead, and then the card came back already in editing when
 *                    the query was cleared, which is a state change nobody
 *                    asked for and nobody could have predicted. The way back
 *                    is to lift the narrowing (Escape's rung 3 clears a
 *                    search), not to act blind.
 *  5. `edit`       — the selected card is already open for editing and the
 *                    caret is not in one of its fields. **Yield — this level
 *                    does not claim Enter.** There is nothing left to descend
 *                    into: the level below this one is the field, and the
 *                    caret is already the way in. Stated so the press is
 *                    deliberately unclaimed rather than silently dead.
 *  6. `read`       — a card is selected, shown in the list, and open for
 *                    reading. Enter it: start the edit session with the caret
 *                    in Term.
 *  7. `pick-first` — nothing is selected. Select the first card for reading.
 *  8. `none`       — nothing to do (an empty deck, or a reader without write
 *                    access).
 *
 * Rung 3 sits above the rest so that a press always acts on the card the reader
 * is looking at. Rung 2 sits above everything but the overlay because a field
 * that owns Enter owns it unconditionally — that is a recorded decision, not a
 * precedence judgement this module gets to make.
 *
 * Like the Escape ladder, this is pure: the caller supplies the state and
 * performs the action, so the order can be unit-tested without a DOM. The
 * `defaultPrevented` check also stays with the caller, for the same reason —
 * it is a fact about a native event, not about screen state.
 */

/** Which level of the screen a single Enter press resolves to. */
export type DeckEnterRung =
  | 'overlay'
  | 'field'
  | 'focus-row'
  | 'out-of-view'
  | 'edit'
  | 'read'
  | 'pick-first'
  | 'none'

/** The screen state the order is decided from. One flag per rung, in rung
 *  order, so a reader can check this type against the list above directly. */
export interface DeckEnterState {
  /** Any modal, sheet, popover or menu layered over the screen. */
  overlayOpen: boolean
  /** The caret is inside a card field (Term or a side-B field). */
  caretInField: boolean
  /** The card id of the row holding DOM focus, or null if focus is not on a
   *  row (the list scrollport, the body, the pane, a control). */
  focusedRowCardId: string | null
  /** The selected card's id, or null when nothing is selected. */
  selectedCardId: string | null
  /** The list is showing the selected card — i.e. the pane can render it.
   *  False while a search or a filter is narrowing the rows past it, which is
   *  a state the screen can be in for as long as the reader leaves the query
   *  there, and which nothing above this ladder may treat as "no selection":
   *  the selection survives the narrowing and comes back when it is lifted.
   *  True for the unsaved draft, which the pane renders although it has no row
   *  of its own — this flag is about what the PANE can show, not about rows. */
  selectionInView: boolean
  /** The selected card is open for EDITING rather than reading — a per-card
   *  edit session, the deck-wide mode, or the unsaved draft. */
  editSessionOpen: boolean
  /** There is at least one card to select. */
  hasCards: boolean
  /** The reader can write to this deck. A system deck never reaches `read`:
   *  there is no edit session to descend into. */
  canEdit: boolean
}

/**
 * Resolves one Enter press to exactly one rung. Total and side-effect free —
 * the caller performs the action for whichever rung comes back, and treats
 * `overlay`, `field`, `out-of-view`, `edit` and `none` as "do not claim this
 * press".
 */
export function resolveDeckEnterRung(state: DeckEnterState): DeckEnterRung {
  if (state.overlayOpen) return 'overlay'
  if (state.caretInField) return 'field'
  if (state.focusedRowCardId !== null && state.focusedRowCardId !== state.selectedCardId) {
    return 'focus-row'
  }
  if (state.selectedCardId !== null) {
    if (!state.selectionInView) return 'out-of-view'
    if (state.editSessionOpen) return 'edit'
    return state.canEdit ? 'read' : 'none'
  }
  return state.hasCards ? 'pick-first' : 'none'
}
